/**
 * Notifications.jsx — In-app notifications for all platform events.
 *
 * Fetches from /api/v1/notifications/ (generic notification system).
 * Marks all as read on page open.
 * Shows icon + colour per notification type.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress,
  Divider, Paper, Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InfoIcon from '@mui/icons-material/Info';
import { apiClient } from '../utils/auth';

// Map notification_type → { icon, color, label }
const TYPE_META = {
  KYC_APPROVED:          { icon: <CheckCircleIcon />,  color: 'success', label: 'KYC Approved' },
  KYC_REJECTED:          { icon: <ErrorOutlineIcon />, color: 'error',   label: 'KYC Rejected' },
  KYC_SUBMITTED:         { icon: <AssignmentIcon />,   color: 'info',    label: 'KYC Submitted' },
  BID_RECEIVED:          { icon: <AssignmentIcon />,   color: 'info',    label: 'New Bid' },
  BID_SHORTLISTED:       { icon: <CheckCircleIcon />,  color: 'success', label: 'Bid Shortlisted' },
  BID_WON:               { icon: <CheckCircleIcon />,  color: 'success', label: 'Bid Won' },
  BID_LOST:              { icon: <InfoIcon />,          color: 'default', label: 'Bid Outcome' },
  DEAL_CREATED:          { icon: <HandshakeIcon />,    color: 'info',    label: 'Deal Created' },
  DEAL_IN_PROGRESS:      { icon: <HandshakeIcon />,    color: 'warning', label: 'Work Started' },
  DEAL_COMPLETED:        { icon: <CheckCircleIcon />,  color: 'success', label: 'Deal Completed' },
  DEAL_CLOSED:           { icon: <CheckCircleIcon />,  color: 'success', label: 'Deal Closed' },
  CONTRACT_INITIATED:    { icon: <DescriptionIcon />,  color: 'info',    label: 'Contract Initiated' },
  CONTRACT_UPLOADED:     { icon: <DescriptionIcon />,  color: 'warning', label: 'Contract Uploaded' },
  CONTRACT_SENT_BIDDER:  { icon: <DescriptionIcon />,  color: 'warning', label: 'Action Required' },
  CONTRACT_APPROVED:     { icon: <CheckCircleIcon />,  color: 'success', label: 'Contract Approved' },
  CONTRACT_REJECTED:     { icon: <ErrorOutlineIcon />, color: 'error',   label: 'Contract Rejected' },
  PAYMENT_RECEIVED:      { icon: <PaymentIcon />,      color: 'success', label: 'Payment Received' },
  PAYMENT_RELEASED:      { icon: <PaymentIcon />,      color: 'success', label: 'Payout Released' },
  PAYOUT_SETUP_REQUIRED: { icon: <PaymentIcon />,      color: 'warning', label: 'Setup Required' },
  ESCROW_CREATED:        { icon: <HandshakeIcon />,    color: 'info',    label: 'Escrow Created' },
  MILESTONE_SUBMITTED:   { icon: <AssignmentIcon />,   color: 'warning', label: 'Milestone Submitted' },
  MILESTONE_CONFIRMED:   { icon: <CheckCircleIcon />,  color: 'info',    label: 'Milestone Confirmed' },
  MILESTONE_RELEASED:    { icon: <PaymentIcon />,      color: 'success', label: 'Milestone Released' },
  DISPUTE_OPENED:        { icon: <GavelIcon />,        color: 'error',   label: 'Dispute Opened' },
  DISPUTE_RESOLVED:      { icon: <GavelIcon />,        color: 'success', label: 'Dispute Resolved' },
  REQUIREMENT_MATCHED:   { icon: <AssignmentIcon />,   color: 'info',    label: 'New Match' },
  GENERAL:               { icon: <InfoIcon />,          color: 'default', label: 'Notification' },
};

function getTypeMeta(type) {
  return TYPE_META[type] || TYPE_META.GENERAL;
}

// MUI Button does NOT support 'default' as a color — map it to 'primary'
function toBtnColor(color) {
  if (!color || color === 'default') return 'primary';
  return color;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/api/v1/notifications/');
        setNotifications(data.notifications || []);
        if (data.unread_count > 0) {
          await apiClient.post('/api/v1/notifications/read/');
          localStorage.setItem('cmp_unread_notifications', '0');
          window.dispatchEvent(new Event('storage'));
        }
      } catch {
        setError('Could not load notifications.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 660, mx: 'auto', mt: 6, p: 3 }}>
      <Typography variant="h4" gutterBottom>Notifications</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {notifications.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
          <NotificationsNoneIcon sx={{ fontSize: 64, mb: 2, opacity: 0.4 }} />
          <Typography variant="h6">No notifications</Typography>
          <Typography variant="body2">You're all caught up.</Typography>
        </Box>
      ) : (
        notifications.map((n) => {
          const meta = getTypeMeta(n.notification_type);
          return (
            <Paper
              key={n.id}
              variant="outlined"
              sx={{
                p: 2.5, mb: 2,
                borderLeft: `4px solid`,
                borderLeftColor: n.is_read
                  ? 'divider'
                  : `${meta.color}.main`,
                opacity: n.is_read ? 0.85 : 1,
                cursor: n.action_url ? 'pointer' : 'default',
                '&:hover': n.action_url ? { bgcolor: 'action.hover' } : {},
              }}
              onClick={() => n.action_url && navigate(n.action_url)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ color: `${meta.color}.main`, mt: 0.3, flexShrink: 0 }}>
                  {meta.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {n.title}
                    </Typography>
                    {!n.is_read && (
                      <Chip label="New" color={meta.color} size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
                    {n.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(n.created_at).toLocaleString()}
                  </Typography>
                  {n.action_url && (
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" variant="text" color={toBtnColor(meta.color)}
                        onClick={e => { e.stopPropagation(); navigate(n.action_url); }}>
                        View →
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          );
        })
      )}
    </Box>
  );
}
